import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@components/atoms/icon';
import { MiniArrowIcon } from '@components/icons/mini-arrow-icon';
import type { DocsVerificationRequirementsPageProps } from '@modules/widget/pages/docs-verification-requirements-page/types';

import { RequirementsPageContainer } from '../styles';

export const DocsVerificationRequirementsPage: FC<DocsVerificationRequirementsPageProps> = ({
    onBack,
}) => {
    const { t } = useTranslation();
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

    const cards = useMemo(
        () => [
            {
                title: t('requirementsPage.docsVer.blurryImageTitle'),
                description: t('requirementsPage.docsVer.blurryImageDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/blurry-image.png',
            },
            {
                title: t('requirementsPage.docsVer.glareOrReflection'),
                description: t('requirementsPage.docsVer.glareOrReflectionDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/glare-or-reflection.png',
            },
            {
                title: t('requirementsPage.docsVer.incompleteDocumentTitle'),
                description: t('requirementsPage.docsVer.incompleteDocumentDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/incomplete-document.png',
            },
            {
                title: t('requirementsPage.docsVer.fadedText'),
                description: t('requirementsPage.docsVer.fadedTextDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/faded-text.png',
            },
            {
                title: t('requirementsPage.docsVer.obstructedInformationTitle'),
                description: t('requirementsPage.docsVer.obstructedInformationTitleDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/obstructed-information.png',
            },
            {
                title: t('requirementsPage.docsVer.incorrectAnglesTitle'),
                description: t('requirementsPage.docsVer.incorrectAnglesDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/incorrect-angles.png',
            },
            {
                title: t('requirementsPage.docsVer.outdatedDocumentTitle'),
                description: t('requirementsPage.docsVer.outdatedDocumentDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/outdated-document.png',
            },
            {
                title: t('requirementsPage.docsVer.lowContrastTitle'),
                description: t('requirementsPage.docsVer.lowContrastDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/low-contrast.png',
            },
            {
                title: t('requirementsPage.docsVer.nonStandardDocumentTitle'),
                description: t('requirementsPage.docsVer.nonStandardDocumentDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/non-standard-document.png',
            },
            {
                title: t('requirementsPage.docsVer.watermarksDetailsTitle'),
                description: t('requirementsPage.docsVer.watermarksDetailsDescription'),
                img: 'https://static.dataspike.io/images/sdk/poi/watermarks-details.png',
            },
        ],
        [t],
    );

    const handleBackClick = () => {
        if (onBack) {
            onBack();
        } else {
            window.history.back();
        }
    };

    return (
        <RequirementsPageContainer isUseWebSDK={!!onBack}>
            {searchParams.get('hideHeader') === 'true' ? (
                <React.Fragment />
            ) : (
                <div className='page-header'>
                    <Icon
                        icon={<MiniArrowIcon className='arrow-icon' width={18} height={14} />}
                        $clickable={true}
                        onClick={handleBackClick}
                    />
                    <h2 className='page-title'>{t('requirementsPage.docsVer.title')}</h2>
                    <div className='page-hidden-elem' />
                </div>
            )}
            <div
                className='page-description'
                dangerouslySetInnerHTML={{ __html: t('requirementsPage.docsVer.description') }}
            />
            <div className='card-container'>
                {cards.map((card, index) => (
                    <React.Fragment key={card.title}>
                        <div className='card'>
                            <img src={card.img} alt={card.title} className='card-img' />
                            <div className='card-title'>{card.title}</div>
                            <div className='card-description'>{card.description}</div>
                        </div>
                        {index % 2 === 1 && index !== cards.length - 1 && (
                            <div className='divider' />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </RequirementsPageContainer>
    );
};
